import axiosClient from "../axios-client";
import { uploadFileApi } from "apis/authServices";
import {
  getAllUsers,
  getApiErrorMessage,
  getUserDetails,
  getUsers,
} from "./userManagmentService";
import {
  GiveawayContest,
  GiveawayContestPayload,
  GiveawayContestQuery,
  GiveawayContestsResponse,
  GiveawayStatus,
  GiveawayUserOption,
  GiveawayWinner,
  GiveawayWinnerPayload,
  GiveawayWinnerSaveResult,
} from "types/giveaway";

/**
 * Giveaway Management services — admin side.
 *
 * Ten endpoints under `/AdminGiveaway` (the axios baseURL already ends in
 * `/api`), all behind the admin JWT and all returning the standard envelope:
 *
 *   { isSuccess, status, message, data, count }
 *
 * The two user-app endpoints (`/Giveaway/GetActiveContest`,
 * `/Giveaway/GetWinnerAnnouncements`) are deliberately not here — there is no
 * user-facing giveaway screen in this app yet.
 */
const BASE = "/AdminGiveaway";

/**
 * Pull the payload out of the response envelope. The axios interceptor already
 * unwrapped `response.data` to the envelope itself, so what arrives here is
 * `{ isSuccess, message, data, ... }`. A few admin endpoints answer flat, hence
 * the fallback.
 */
const unwrap = (response: any): any =>
  response && typeof response === "object" && "data" in response
    ? response.data
    : response;

/** The backend's own `message`, when it sent one worth showing. */
const envelopeMessage = (response: any): string | undefined =>
  typeof response?.message === "string" && response.message.trim()
    ? (response.message as string)
    : undefined;

// The API may return dates as "2026-07-31T00:00:00" or "2026-07-31"; both are
// fine for dayjs, so the mapper only guards against nulls and key casing.
const pick = <T>(source: any, ...keys: string[]): T | undefined => {
  for (const key of keys) {
    if (source?.[key] !== undefined && source?.[key] !== null) {
      return source[key] as T;
    }
  }
  return undefined;
};

/** Pick a positive numeric value, ignoring hyphens, empty strings, and 0s. */
const pickNumber = (source: any, ...keys: string[]): number | undefined => {
  const val = pick<any>(source, ...keys);
  if (val === undefined || val === null || val === "" || val === "-") return undefined;
  const num = Number(val);
  return !isNaN(num) && num > 0 ? num : undefined;
};

/**
 * One prize slot off a contest. The backend may express prizes either as a
 * `prizes[]` array keyed by position or as numbered fields, so both are read;
 * position 1 also falls back to the original single-prize `reward`/
 * `tokenReward` pair.
 */
const prizeAt = (raw: any, position: number): any | undefined => {
  const prizes = pick<any[]>(raw, "prizes", "Prizes");
  if (!Array.isArray(prizes)) return undefined;
  return prizes.find(
    (prize) => Number(pick<number>(prize, "position", "Position")) === position,
  );
};

/** Normalise a raw API contest into the shape the UI expects. */
export const mapContest = (raw: any): GiveawayContest => {
  const rawStatus = pick<string>(raw, "status", "Status");
  const startStr = pick<string>(raw, "startDate", "StartDate");
  const endStr = pick<string>(raw, "endDate", "EndDate");

  return {
    contestId: Number(pick<number>(raw, "contestId", "ContestId", "id") ?? 0),
    contestName: String(
      pick<string>(raw, "contestName", "ContestName", "name") ?? "",
    ),
    startDate: String(startStr ?? ""),
    endDate: String(endStr ?? ""),
    reward1st:
      pick<string>(prizeAt(raw, 1), "reward", "Reward") ??
      pick<string>(raw, "reward1st", "Reward1st", "reward", "Reward", "prize"),
    reward2nd:
      pick<string>(prizeAt(raw, 2), "reward", "Reward") ??
      pick<string>(raw, "reward2nd", "Reward2nd"),
    reward3rd:
      pick<string>(prizeAt(raw, 3), "reward", "Reward") ??
      pick<string>(raw, "reward3rd", "Reward3rd"),
    additionalWinnersCount:
      pick<number>(
        raw,
        "additionalWinnersCount",
        "AdditionalWinnersCount",
      ) ?? null,
    tokenRewardAdditionalWinners:
      pickNumber(
        raw,
        "tokenRewardAdditionalWinners",
        "TokenRewardAdditionalWinners",
      ) ?? null,
    tokenValidityPeriod:
      pickNumber(
        raw,
        "tokenValidityPeriod",
        "TokenValidityPeriod",
        "tokenValidity",
        "TokenValidity",
      ) ?? null,
    // Null validity means "no expiry" — the backend skips its expiry logic.
    tokenValidityFrom: pick<string>(
      raw,
      "tokenValidityFrom",
      "TokenValidityFrom",
    ),
    tokenValidityTo: pick<string>(raw, "tokenValidityTo", "TokenValidityTo"),
    instagramUrl: pick<string>(raw, "instagramUrl", "InstagramUrl"),
    linkedInUrl: pick<string>(raw, "linkedInUrl", "LinkedInUrl", "linkedinUrl"),
    rules: pick<string>(raw, "rules", "Rules"),
    thumbnailUrl: pick<string>(
      raw,
      "thumbnailUrl",
      "ThumbnailUrl",
      "thumbnailImage",
      "ThumbnailImage",
      "thumbnail",
      "Thumbnail",
      "thumbnailPath",
      "ThumbnailPath",
      "imageUrl",
      "ImageUrl",
      "image",
      "Image",
      "imagePath",
      "ImagePath",
      "filePath",
      "FilePath",
      "fileUrl",
      "FileUrl",
    ),
    resultAnnouncementDate: pick<string>(
      raw,
      "resultAnnouncementDate",
      "ResultAnnouncementDate",
    ),
    status: (rawStatus as GiveawayStatus) || "Upcoming",
    totalParticipants:
      pick<number>(raw, "totalParticipants", "TotalParticipants") ?? null,
    winnersCount: pick<number>(raw, "winnersCount", "WinnersCount"),
  };
};

/**
 * Winner rows carry the user inline on the list endpoint and may nest them
 * under `user` elsewhere, so both are read.
 */
const mapWinner = (raw: any): GiveawayWinner => {
  const user = pick<any>(raw, "user", "User") ?? raw;

  // GetGiveawayWinners/GetGiveawayWinner embed the user flat, prefixed with
  // `user` (userDisplayName/userFirstName/userLastName/userEmail/
  // userProfilePictureUrl — see GiveawayWinnerModel), not nested or bare —
  // those prefixed keys must be checked first or every one of these silently
  // falls back to empty/undefined.
  const firstLast = [
    pick<string>(user, "userFirstName"),
    pick<string>(user, "userLastName"),
  ]
    .filter(Boolean)
    .join(" ");

  return {
    winnerId: Number(pick<number>(raw, "winnerId", "WinnerId", "id") ?? 0),
    userId: pick<number>(user, "userId", "UserId", "id"),
    userName: String(
      pick<string>(
        user,
        "userDisplayName",
        // This API spells it lower-case `username` on every other user-bearing
        // endpoint (User Management, feedback details); `displayName` is what
        // the public winner feed uses.
        "username",
        "userName",
        "UserName",
        "displayName",
        "fullName",
        "name",
      ) ?? firstLast ?? "",
    ),
    email: pick<string>(user, "userEmail", "email", "Email"),
    profilePictureUrl: pick<string>(
      user,
      "userProfilePictureUrl",
      "profilePictureUrl",
      "ProfilePictureUrl",
      "profilePicture",
      "profilePic",
    ),
    position: pick<number>(raw, "position", "Position", "rank") ?? null,
    isAdditionalWinner: Boolean(
      pick<boolean>(raw, "isAdditionalWinner", "IsAdditionalWinner") ?? false,
    ),
    reward: pick<string>(raw, "reward", "Reward", "prize"),
    tokensCredited: pick<number>(raw, "tokensCredited", "TokensCredited"),
    announcedOn: pick<string>(
      raw,
      "announcedOn",
      "AnnouncedOn",
      "announcedDate",
    ),
    displayFrom: pick<string>(raw, "displayFrom", "DisplayFrom", "displayStart"),
    displayTo: pick<string>(raw, "displayTo", "DisplayTo", "displayEnd"),
    status: pick<string>(raw, "status", "Status"),
    creditError: pick<string>(raw, "creditError", "CreditError"),
  };
};

/** Rows out of a list payload, whichever key the endpoint used. */
const listOf = (payload: any, ...keys: string[]): any[] => {
  if (Array.isArray(payload)) return payload;
  return pick<any[]>(payload, ...keys) ?? [];
};

/** A contest is archived once it has finished — nothing else moves it. */
const isConcluded = (contest: GiveawayContest) =>
  contest.status === "Completed" || contest.status === "Cancelled";

/**
 * The two contest lists the page renders, split by the backend: `latest` holds
 * the live and scheduled contests, `previous` the concluded ones. The paging
 * fields describe `previous`.
 *
 * Two older shapes are still accepted, since neither costs anything to support:
 * a flat array of contests (split here by status) and a single `latest` object
 * rather than an array.
 */
export const getGiveawayContests = async (
  query: GiveawayContestQuery = {},
): Promise<GiveawayContestsResponse> => {
  const { searchTerm = "", status = "", page = 1, size = 10 } = query;

  // Empty filters are left off the query string rather than sent blank —
  // `status=` is bound to an enum server-side and an empty value is not one.
  const params: Record<string, string | number> = { page, size };
  if (searchTerm.trim()) params.searchTerm = searchTerm.trim();
  if (status) params.status = status;

  const response: any = await axiosClient.get(`${BASE}/GetGiveawayContests`, {
    params,
  });
  const payload = unwrap(response);

  // Flat array response: split it ourselves.
  if (Array.isArray(payload)) {
    const contests = payload.map(mapContest);
    const previous = contests.filter(isConcluded);
    return {
      latest: contests.filter((contest) => !isConcluded(contest)),
      previous,
      totalCount: previous.length,
      page,
      pageSize: size,
    };
  }

  const rawLatest = pick<any>(payload, "latest", "Latest", "latestContest");
  const rawPrevious = listOf(payload, "previous", "Previous", "previousContests");

  return {
    // Tolerate the single-contest shape this endpoint used to return.
    latest: (Array.isArray(rawLatest)
      ? rawLatest
      : rawLatest
        ? [rawLatest]
        : []
    ).map(mapContest),
    previous: rawPrevious.map(mapContest),
    totalCount: Number(
      pick<number>(payload, "totalCount", "TotalCount") ??
        response?.count ??
        rawPrevious.length,
    ),
    page: Number(pick<number>(payload, "page", "Page") ?? page),
    pageSize: Number(pick<number>(payload, "pageSize", "PageSize") ?? size),
  };
};

/**
 * Fill in winners whose row carried a userId but no name.
 *
 * GetGiveawayWinners is documented as embedding the user, but in practice the
 * name and email come back empty, which left the winner screens showing bare
 * ids. Admin user details carry `fullName`, so the few missing names are looked
 * up directly — at most one request per prize, and none once the winner
 * endpoint starts sending them.
 */
const fillMissingWinnerNames = async (
  winners: GiveawayWinner[],
): Promise<GiveawayWinner[]> => {
  const missing = winners.filter(
    (winner) => !winner.userName.trim() && !!winner.userId,
  );
  if (!missing.length) return winners;

  const names = new Map<number, { userName?: string; email?: string }>();
  await Promise.all(
    missing.map(async (winner) => {
      try {
        const response: any = await getUserDetails(winner.userId!);
        const details = unwrap(response);
        names.set(winner.userId!, {
          userName: pick<string>(details, "fullName", "username", "userName"),
          email: pick<string>(details, "email", "Email"),
        });
      } catch (error) {
        // A winner whose user can't be read still renders, just as their id.
        console.error(`Failed to resolve winner ${winner.userId}:`, error);
      }
    }),
  );

  return winners.map((winner) => {
    const resolved = winner.userId ? names.get(winner.userId) : undefined;
    if (!resolved) return winner;
    return {
      ...winner,
      userName: resolved.userName ?? winner.userName,
      email: winner.email ?? resolved.email,
    };
  });
};

/** Winners of a single contest, ordered by position. */
export const getGiveawayWinners = async (
  contestId: number,
): Promise<GiveawayWinner[]> => {
  const response: any = await axiosClient.get(`${BASE}/GetGiveawayWinners`, {
    params: { contestId },
  });
  const winners = listOf(
    unwrap(response),
    "items",
    "winners",
    "Winners",
  ).map(mapWinner);

  return fillMissingWinnerNames(winners);
};

/** Full detail for a single contest. */
export const getGiveawayContestById = async (
  contestId: number,
): Promise<GiveawayContest | null> => {
  const response: any = await axiosClient.get(`${BASE}/GetGiveawayContest`, {
    params: { contestId },
  });
  const payload = unwrap(response);
  return payload ? mapContest(payload) : null;
};

/**
 * The thumbnail is an optional file upload, so a contest with a new image has
 * to go up as multipart/form-data. Without one we send plain JSON — which is
 * what the endpoints prefer, and both are accepted.
 */
const buildRequest = (payload: GiveawayContestPayload) => {
  const { thumbnail, ...fields } = payload;

  if (!thumbnail) {
    return { body: fields as Record<string, unknown>, config: undefined };
  }

  const formData = new FormData();
  Object.entries(fields).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      formData.append(key, String(value));
    }
  });
  formData.append("thumbnail", thumbnail);

  return {
    body: formData,
    config: { headers: { "Content-Type": "multipart/form-data" } },
  };
};

export const addGiveawayContest = async (payload: GiveawayContestPayload) => {
  if (payload.thumbnail) {
    const formData = new FormData();
    const { thumbnail, ...fields } = payload;
    Object.entries(fields).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(key, String(value));
      }
    });
    formData.append("thumbnail", thumbnail);
    return axiosClient.post(`${BASE}/AddGiveawayContest`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  }

  const { thumbnail, ...fields } = payload;
  return axiosClient.post(`${BASE}/AddGiveawayContest`, fields);
};

/**
 * Update rejects a start date change on a contest that is already live, and any
 * end date before the start; the backend answers with the reason in `message`.
 * Omitted fields keep their stored value.
 */
export const updateGiveawayContest = async (
  payload: GiveawayContestPayload,
) => {
  if (payload.thumbnail) {
    const formData = new FormData();
    const { thumbnail, ...fields } = payload;
    Object.entries(fields).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(key, String(value));
      }
    });
    formData.append("thumbnail", thumbnail);
    return axiosClient.post(`${BASE}/UpdateGiveawayContest`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  }

  const { thumbnail, ...fields } = payload;
  return axiosClient.post(`${BASE}/UpdateGiveawayContest`, fields);
};

/** Soft delete — the contest's winner rows are preserved for audit. */
export const deleteGiveawayContest = async (contestId: number) =>
  axiosClient.delete(`${BASE}/DeleteGiveawayContest`, { params: { contestId } });

/** Retry token credit for a CreditFailed winner */
export const retryGiveawayWinnerCredit = async (winnerId: number) =>
  axiosClient.post(`${BASE}/RetryGiveawayWinnerCredit`, null, {
    params: { winnerId },
  });

/** A single winner, for the winner edit screen. */
export const getGiveawayWinnerById = async (
  winnerId: number,
): Promise<GiveawayWinner | null> => {
  const response: any = await axiosClient.get(`${BASE}/GetGiveawayWinner`, {
    params: { winnerId },
  });
  const payload = unwrap(response);
  return payload ? mapWinner(payload) : null;
};

/**
 * Publish (add) or re-assign (update) a winner announcement.
 *
 * Add takes `{ contestId, position, userId }` and credits the contest's
 * `tokenReward` in the same transaction — the response carries how many tokens
 * went out. It rejects a second winner at the same position on a contest.
 *
 * Update only takes `{ winnerId, userId }`: the same user is a no-op, a
 * different one credits the new winner without clawing back from the old one.
 * Changing position or contest is a delete + add, which is why neither is sent.
 *
 * `displayFrom`/`displayTo` are captured by the form but are not part of the
 * documented contract yet — they ride along so nothing is lost once the backend
 * accepts them, and are ignored until then.
 */
export const saveGiveawayWinner = async (
  payload: GiveawayWinnerPayload,
): Promise<GiveawayWinnerSaveResult> => {
  const {
    winnerId,
    contestId,
    userId,
    position,
    isAdditionalWinner,
    displayFrom,
    displayTo,
  } = payload;

  const isUpdate = !!winnerId;
  const body = isUpdate
    ? { winnerId, userId, displayFrom, displayTo }
    : { contestId, position, isAdditionalWinner, userId, displayFrom, displayTo };

  const response: any = await axiosClient.post(
    `${BASE}/${isUpdate ? "UpdateGiveawayWinner" : "AddGiveawayWinner"}`,
    body,
  );
  const data = unwrap(response) ?? {};

  return {
    winnerId: Number(
      pick<number>(data, "winnerId", "WinnerId") ?? winnerId ?? 0,
    ),
    tokensCredited: Number(
      pick<number>(data, "tokensCredited", "TokensCredited") ?? 0,
    ),
    message: envelopeMessage(response),
  };
};

/** Soft delete — tokens already credited stay with the user. */
export const deleteGiveawayWinner = async (winnerId: number) =>
  axiosClient.delete(`${BASE}/DeleteGiveawayWinner`, { params: { winnerId } });

/** One winner slot on the winner announcement screen — a prize tier or an additional winner. */
export interface GiveawayWinnerDraft {
  /** Present once the slot has been published. */
  winnerId?: number;
  /** 1/2/3 for a prize tier, null/undefined for an Additional Winner. */
  position?: number | null;
  isAdditionalWinner?: boolean;
  /** null clears the slot — for a published winner that means remove them. */
  userId: number | null;
  displayFrom?: string;
  displayTo?: string;
}

export interface GiveawayWinnerPublishResult {
  tokensCredited: number;
  published: number;
  removed: number;
  /** One entry per slot the API refused, with the reason it gave. */
  failures: Array<{ position?: number | null; message: string }>;
}

/**
 * Publish a contest's whole winner list in one action.
 *
 * The API takes one winner per call, so this walks the prizes — but every prize
 * is attempted even when an earlier one is rejected, and the reasons come back
 * together. Aborting on the first failure would leave the admin with a single
 * error and no idea whether the other prizes were even tried.
 *
 * Re-sending an unchanged winner is safe — UpdateGiveawayWinner treats the same
 * user and window as a no-op, so there's no need to diff before calling.
 */
export const publishGiveawayWinners = async (
  contestId: number,
  drafts: GiveawayWinnerDraft[],
): Promise<GiveawayWinnerPublishResult> => {
  const result: GiveawayWinnerPublishResult = {
    tokensCredited: 0,
    published: 0,
    removed: 0,
    failures: [],
  };

  for (const draft of drafts) {
    try {
      if (!draft.userId) {
        if (draft.winnerId) {
          await deleteGiveawayWinner(draft.winnerId);
          result.removed += 1;
        }
        continue;
      }

      const saved = await saveGiveawayWinner({
        ...(draft.winnerId ? { winnerId: draft.winnerId } : {}),
        contestId,
        userId: draft.userId,
        position: draft.position ?? null,
        isAdditionalWinner: draft.isAdditionalWinner,
        displayFrom: draft.displayFrom,
        displayTo: draft.displayTo,
      });

      result.tokensCredited += saved.tokensCredited ?? 0;
      result.published += 1;
    } catch (error) {
      console.error(`Failed to publish winner slot ${draft.position ?? "additional"}:`, error);
      result.failures.push({
        position: draft.position ?? null,
        message: getApiErrorMessage(error, "could not be published"),
      });
    }
  }

  return result;
};

/**
 * Users the admin can pick as a winner. Delegates to the existing admin user
 * search — only the fields the picker needs are kept.
 */
export const searchWinnerCandidates = async (
  searchTerm: string,
): Promise<GiveawayUserOption[]> => {
  const response: any = await getUsers(searchTerm, "", 1, 10);
  // GetUsers splits its page into per-role buckets; the picker wants every user
  // regardless of the group they are in.
  const items: any[] = Array.isArray(response) ? response : getAllUsers(response);

  return items.map((raw) => ({
    userId: Number(pick<number>(raw, "userId", "UserId", "id") ?? 0),
    userName: String(
      pick<string>(raw, "username", "userName", "fullName", "name") ?? "",
    ),
    email: pick<string>(raw, "email", "Email"),
    // GetUsers (AdminUserRepository) returns this as `ProfilePic`.
    profilePictureUrl: pick<string>(
      raw,
      "profilePic",
      "ProfilePic",
      "profilePictureUrl",
      "ProfilePictureUrl",
    ),
  }));
};

/**
 * Fetch latest giveaway contests for user side.
 * Endpoint: GET /api/Giveaway/GetLatestContests
 */
export const getLatestContests = async (): Promise<GiveawayContest[]> => {
  try {
    const response: any = await axiosClient.get("/Giveaway/GetLatestContests");
    const items = Array.isArray(response?.data)
      ? response.data
      : Array.isArray(response)
        ? response
        : [];
    return items.map(mapContest);
  } catch (error) {
    console.error("Failed to fetch latest giveaway contests:", error);
    return [];
  }
};

/**
 * Fetch contest winner badges for user side.
 * Endpoint: GET /api/Giveaway/GetWinnerBadges
 */
export const getWinnerBadges = async (): Promise<import("types/giveaway").ContestWinnerBadgeGroup[]> => {
  try {
    const response: any = await axiosClient.get("/Giveaway/GetWinnerBadges");
    const items = Array.isArray(response?.data)
      ? response.data
      : Array.isArray(response)
        ? response
        : [];
    return items;
  } catch (error) {
    console.error("Failed to fetch winner badges:", error);
    return [];
  }
};
